import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import { useAuth } from '../auth/ContextoAuth'
import {
    autenticacion,
    devoluciones as apiDevoluciones,
    ventas as apiVentas,
} from '../api/servicios'
import { cantidad as formatearCantidad, fechaHora, dinero } from '../api/formato'
import type { Devolucion, Venta } from '../api/tipos'
import { Dialogo } from '../componentes/Dialogo'
import { Estado } from '../componentes/Estado'
import { Paginador, usarRetardo } from '../componentes/Paginador'
import { mensajeDeError, usarCargar } from '../componentes/usarCargar'
import './listados.css'

/**
 * Devoluciones de producto danado de fabrica.
 *
 * El producto **no vuelve al stock vendible**: se le devuelve el dinero al
 * cliente y la mercaderia se da por perdida. Por eso la pantalla no ofrece
 * ninguna opcion de reingreso a inventario.
 */
export function Devoluciones() {
    const { puede } = useAuth()
    const puedeAnular = puede('devolucion.autorizar')

    const [estado, setEstado] = useState('')
    const [pagina, setPagina] = useState(1)
    const [nueva, setNueva] = useState(false)
    const [abierta, setAbierta] = useState<Devolucion | null>(null)
    const [anulando, setAnulando] = useState<Devolucion | null>(null)

    const lista = usarCargar(
        () => apiDevoluciones.listar({ estado: estado || undefined, pagina }),
        [estado, pagina],
    )

    return (
        <div className="pagina-listado columna g4">
            <header className="fila entre envuelve g3">
                <h1>Devoluciones</h1>

                <button
                    type="button"
                    className="boton boton-primario"
                    onClick={() => setNueva(true)}
                >
                    Nueva devolución
                </button>
            </header>

            <section className="panel columna g3">
                <div className="filtros">
                    <select
                        className="campo"
                        aria-label="Estado"
                        value={estado}
                        onChange={(e) => {
                            setEstado(e.target.value)
                            setPagina(1)
                        }}
                    >
                        <option value="">Todos los estados</option>
                        <option value="Borrador">Borrador</option>
                        <option value="Confirmada">Confirmada</option>
                        <option value="Anulada">Anulada</option>
                    </select>
                </div>

                <Estado
                    cargando={lista.cargando}
                    error={lista.error}
                    alReintentar={lista.recargar}
                    vacio={(lista.datos?.items ?? []).length === 0}
                    mensajeVacio="No hay devoluciones registradas."
                >
                    <div className="tabla-envoltura">
                        <table className="tabla">
                            <thead>
                                <tr>
                                    <th>Número</th>
                                    <th>Venta</th>
                                    <th className="oculto-angosto">Fecha</th>
                                    <th className="derecha">Total</th>
                                    <th>Estado</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {(lista.datos?.items ?? []).map((d) => (
                                    <tr key={d.devolucionId}>
                                        <td className="numero chico">{d.numero}</td>
                                        <td>
                                            <div className="columna">
                                                <span className="numero chico">
                                                    {d.ventaNumero ?? `#${d.ventaId}`}
                                                </span>
                                                <span className="tenue diminuto truncar">
                                                    {d.motivo}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="oculto-angosto tenue chico">
                                            {fechaHora(d.registradaEn)}
                                        </td>
                                        <td className="derecha numero">{dinero(d.total)}</td>
                                        <td>
                                            <span
                                                className={`insignia ${
                                                    d.estado === 'Confirmada'
                                                        ? 'insignia-ok'
                                                        : d.estado === 'Anulada'
                                                          ? 'insignia-peligro'
                                                          : 'insignia-atencion'
                                                }`}
                                            >
                                                {d.estado}
                                            </span>
                                        </td>
                                        <td className="derecha">
                                            <div className="fila g2 acciones-fila">
                                                <button
                                                    type="button"
                                                    className="boton boton-fantasma"
                                                    onClick={() => setAbierta(d)}
                                                >
                                                    {d.estado === 'Borrador' ? 'Continuar' : 'Ver'}
                                                </button>

                                                {puedeAnular && d.estado === 'Confirmada' && (
                                                    <button
                                                        type="button"
                                                        className="boton boton-fantasma boton-peligro"
                                                        onClick={() => setAnulando(d)}
                                                    >
                                                        Anular
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Estado>

                <Paginador pagina={lista.datos} alCambiar={setPagina} />
            </section>

            {nueva && (
                <DialogoNuevaDevolucion
                    alCreada={(devolucion) => {
                        setNueva(false)
                        setAbierta(devolucion)
                    }}
                    alCerrar={() => setNueva(false)}
                />
            )}

            {abierta && (
                <DialogoDetalle
                    devolucion={abierta}
                    alCerrar={() => {
                        setAbierta(null)
                        lista.recargar()
                    }}
                />
            )}

            {anulando && (
                <DialogoAnularDevolucion
                    devolucion={anulando}
                    alAnulada={() => {
                        setAnulando(null)
                        lista.recargar()
                    }}
                    alCerrar={() => setAnulando(null)}
                />
            )}
        </div>
    )
}

/* -------------------------------------------------------------------------- */
/* Alta: elegir la venta                                                       */
/* -------------------------------------------------------------------------- */

function DialogoNuevaDevolucion({
    alCreada,
    alCerrar,
}: {
    alCreada: (devolucion: Devolucion) => void
    alCerrar: () => void
}) {
    const { puede } = useAuth()
    const puedeBuscarVentas = puede('venta.crear')

    const [texto, setTexto] = useState('')
    const [elegida, setElegida] = useState<Venta | null>(null)
    const [motivo, setMotivo] = useState('')
    const [error, setError] = useState<string | null>(null)
    const [enviando, setEnviando] = useState(false)

    const busqueda = usarRetardo(texto)

    // Solo se devuelve contra una venta cobrada y vigente, y el filtro de estado
    // del backend admite un valor a la vez: se piden los dos estados en
    // paralelo. Filtrar aqui evita que el cajero elija una venta anulada y
    // reciba el error recien al crear la devolucion.
    const ventas = usarCargar<Venta[] | null>(
        () =>
            puedeBuscarVentas && busqueda.trim().length >= 2
                ? Promise.all([
                      apiVentas.buscar({ texto: busqueda.trim(), estado: 'Completada' }),
                      apiVentas.buscar({ texto: busqueda.trim(), estado: 'DevueltaParcial' }),
                  ]).then(([completadas, parciales]) => [
                      ...completadas.items,
                      ...parciales.items,
                  ])
                : Promise.resolve(null),
        [busqueda, puedeBuscarVentas],
    )

    const encontradas = ventas.datos ?? []

    async function crear(evento: FormEvent) {
        evento.preventDefault()
        setError(null)

        if (!elegida) {
            setError('Elija la venta que se devuelve.')
            return
        }

        if (motivo.trim().length === 0) {
            setError('El motivo es obligatorio: una devolución sin causa no se puede revisar.')
            return
        }

        setEnviando(true)

        try {
            alCreada(await apiDevoluciones.crear(elegida.ventaId, motivo.trim()))
        } catch (fallo) {
            setError(mensajeDeError(fallo))
        } finally {
            setEnviando(false)
        }
    }

    return (
        <Dialogo
            titulo="Nueva devolución"
            alCerrar={alCerrar}
            acciones={
                <>
                    <button type="button" className="boton" onClick={alCerrar}>
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        form="forma-nueva-devolucion"
                        className="boton boton-primario"
                        disabled={enviando}
                    >
                        {enviando ? 'Creando…' : 'Continuar'}
                    </button>
                </>
            }
        >
            <form id="forma-nueva-devolucion" className="columna g4" onSubmit={crear}>
                {!puedeBuscarVentas ? (
                    <div className="aviso aviso-atencion">
                        Para buscar la venta original hace falta el permiso de registrar ventas.
                        Pida a un supervisor que inicie la devolución.
                    </div>
                ) : elegida ? (
                    <div className="fila entre g3">
                        <div className="columna">
                            <strong className="numero">
                                {elegida.numero ?? `#${elegida.ventaId}`}
                            </strong>
                            <span className="tenue chico">
                                {fechaHora(elegida.creadaEn)} · {dinero(elegida.total)}
                            </span>
                        </div>
                        <button
                            type="button"
                            className="boton boton-fantasma"
                            onClick={() => setElegida(null)}
                        >
                            Cambiar
                        </button>
                    </div>
                ) : (
                    <div>
                        <label className="etiqueta" htmlFor="buscar-venta">
                            Venta
                        </label>
                        <input
                            id="buscar-venta"
                            className="campo numero"
                            type="search"
                            autoComplete="off"
                            placeholder="Número de la venta"
                            value={texto}
                            onChange={(e) => setTexto(e.target.value)}
                        />

                        {ventas.error && (
                            <div
                                className="aviso aviso-error"
                                role="alert"
                                style={{ marginTop: 'var(--e2)' }}
                            >
                                {ventas.error}
                            </div>
                        )}

                        {busqueda.trim().length >= 2 &&
                            !ventas.cargando &&
                            !ventas.error &&
                            encontradas.length === 0 && (
                                <p className="tenue diminuto" style={{ marginTop: 'var(--e1)' }}>
                                    Ninguna venta cobrada coincide con ese número.
                                </p>
                            )}

                        {encontradas.length > 0 && (
                            <ul className="sugerencias">
                                {encontradas.map((v) => (
                                    <li key={v.ventaId}>
                                        <button type="button" onClick={() => setElegida(v)}>
                                            <span className="fila entre g3">
                                                <span className="numero">
                                                    {v.numero ?? `#${v.ventaId}`}
                                                </span>
                                                <span className="numero chico">
                                                    {dinero(v.total)}
                                                </span>
                                            </span>
                                            <span className="tenue diminuto">
                                                {fechaHora(v.creadaEn)}
                                                {v.estado === 'DevueltaParcial' &&
                                                    ' · ya tiene devoluciones'}
                                            </span>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}

                <div>
                    <label className="etiqueta" htmlFor="motivo-devolucion">
                        Motivo
                    </label>
                    <input
                        id="motivo-devolucion"
                        className="campo"
                        maxLength={500}
                        placeholder="Producto dañado de fábrica"
                        value={motivo}
                        onChange={(e) => setMotivo(e.target.value)}
                    />
                </div>

                {error && (
                    <div className="aviso aviso-error" role="alert">
                        {error}
                    </div>
                )}
            </form>
        </Dialogo>
    )
}

/* -------------------------------------------------------------------------- */
/* Detalle: lineas y confirmacion                                              */
/* -------------------------------------------------------------------------- */

function DialogoDetalle({
    devolucion: inicial,
    alCerrar,
}: {
    devolucion: Devolucion
    alCerrar: () => void
}) {
    const { puede } = useAuth()
    const [devolucion, setDevolucion] = useState(inicial)
    const [error, setError] = useState<string | null>(null)
    const [ocupado, setOcupado] = useState(false)
    const [confirmando, setConfirmando] = useState(false)

    const editable = devolucion.estado === 'Borrador'

    // La venta trae lo vendido; la devolucion, lo ya devuelto. Hacen falta las
    // dos para saber que queda por devolver de cada linea.
    const venta = usarCargar(
        () => (editable && puede('venta.crear') ? apiVentas.obtener(devolucion.ventaId) : Promise.resolve(null)),
        [devolucion.ventaId, editable],
    )

    async function ejecutar(accion: () => Promise<Devolucion>): Promise<boolean> {
        setError(null)
        setOcupado(true)

        try {
            setDevolucion(await accion())
            return true
        } catch (fallo) {
            setError(mensajeDeError(fallo))
            return false
        } finally {
            setOcupado(false)
        }
    }

    return (
        <Dialogo
            titulo={`Devolución ${devolucion.numero}`}
            ancho="ancho"
            alCerrar={alCerrar}
            acciones={
                <>
                    <button type="button" className="boton" onClick={alCerrar}>
                        Cerrar
                    </button>

                    {editable && !confirmando && (
                        <button
                            type="button"
                            className="boton boton-primario"
                            disabled={ocupado || devolucion.lineas.length === 0}
                            onClick={() => setConfirmando(true)}
                        >
                            Confirmar y reembolsar
                        </button>
                    )}
                </>
            }
        >
            <div className="columna g4">
                <div className="fila entre envuelve g3">
                    <span className="tenue chico">
                        Venta {devolucion.ventaNumero ?? `#${devolucion.ventaId}`} ·{' '}
                        {fechaHora(devolucion.registradaEn)}
                    </span>
                    <span
                        className={`insignia ${
                            devolucion.estado === 'Confirmada'
                                ? 'insignia-ok'
                                : devolucion.estado === 'Anulada'
                                  ? 'insignia-peligro'
                                  : 'insignia-atencion'
                        }`}
                    >
                        {devolucion.estado}
                    </span>
                </div>

                <p className="chico">{devolucion.motivo}</p>

                {devolucion.lineas.length === 0 ? (
                    <p className="vacio">Todavía no hay productos en esta devolución.</p>
                ) : (
                    <div className="tabla-envoltura">
                        <table className="tabla">
                            <thead>
                                <tr>
                                    <th>Producto</th>
                                    <th className="derecha">Cantidad</th>
                                    <th className="derecha">Importe</th>
                                    {editable && <th />}
                                </tr>
                            </thead>
                            <tbody>
                                {devolucion.lineas.map((l) => (
                                    <tr key={l.devolucionDetalleId}>
                                        <td>
                                            <div className="columna">
                                                <span>{l.descripcion}</span>
                                                <span className="tenue diminuto numero">
                                                    {l.sku} · {l.condicionProducto}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="derecha numero">
                                            {formatearCantidad(l.cantidad, 3)}
                                        </td>
                                        <td className="derecha numero">{dinero(l.importe)}</td>
                                        {editable && (
                                            <td className="derecha">
                                                <button
                                                    type="button"
                                                    className="boton boton-fantasma boton-peligro"
                                                    disabled={ocupado}
                                                    onClick={() =>
                                                        ejecutar(() =>
                                                            apiDevoluciones.quitarLinea(
                                                                devolucion.devolucionId,
                                                                l.devolucionDetalleId,
                                                            ),
                                                        )
                                                    }
                                                >
                                                    Quitar
                                                </button>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                <div className="fila entre g3">
                    <span className="negrita">Total a devolver</span>
                    <span className="numero negrita">{dinero(devolucion.total)}</span>
                </div>

                {devolucion.reembolsos.length > 0 && (
                    <section className="columna g2">
                        <h3>Reembolsado</h3>
                        <ul className="lista-simple">
                            {devolucion.reembolsos.map((r) => (
                                <li key={r.reembolsoDevolucionId} className="fila entre g3">
                                    <span className="chico">
                                        {r.medioPagoCodigo}
                                        {r.referencia && (
                                            <span className="tenue diminuto"> · {r.referencia}</span>
                                        )}
                                    </span>
                                    <span className="numero chico">{dinero(r.importe)}</span>
                                </li>
                            ))}
                        </ul>
                    </section>
                )}

                {editable && !confirmando && (
                    <AgregarLineas
                        devolucion={devolucion}
                        venta={venta.datos ?? null}
                        cargandoVenta={venta.cargando}
                        errorVenta={venta.error}
                        alReintentarVenta={venta.recargar}
                        ocupado={ocupado}
                        alAgregar={(cuerpo) =>
                            ejecutar(() =>
                                apiDevoluciones.agregarLinea(devolucion.devolucionId, cuerpo),
                            )
                        }
                    />
                )}

                {editable && confirmando && (
                    <FormularioReembolso
                        devolucion={devolucion}
                        alConfirmada={(actualizada) => {
                            setDevolucion(actualizada)
                            setConfirmando(false)
                        }}
                        alCancelar={() => setConfirmando(false)}
                    />
                )}

                {error && (
                    <div className="aviso aviso-error" role="alert">
                        {error}
                    </div>
                )}
            </div>
        </Dialogo>
    )
}

interface PropsAgregar {
    devolucion: Devolucion
    venta: Venta | null
    cargandoVenta: boolean
    errorVenta: string | null
    alReintentarVenta: () => void
    ocupado: boolean
    alAgregar: (cuerpo: {
        ventaDetalleId: number
        cantidad: number
        condicionProducto?: string
    }) => Promise<boolean>
}

function AgregarLineas({
    devolucion,
    venta,
    cargandoVenta,
    errorVenta,
    alReintentarVenta,
    ocupado,
    alAgregar,
}: PropsAgregar) {
    const [detalleId, setDetalleId] = useState('')
    const [cantidad, setCantidad] = useState('')
    const [condicion, setCondicion] = useState('')
    const [error, setError] = useState<string | null>(null)

    const linea = venta?.lineas.find((l) => String(l.ventaDetalleId) === detalleId)

    // Tope visible: lo vendido menos lo que esta devolucion ya lleva de esa
    // linea. El backend descuenta ademas lo devuelto en otras devoluciones, y
    // rechaza el exceso; esto solo evita el intento obvio.
    const yaDevuelto = devolucion.lineas
        .filter((l) => String(l.ventaDetalleId) === detalleId)
        .reduce((suma, l) => suma + l.cantidad, 0)

    const disponible = linea ? linea.cantidad - yaDevuelto : 0

    async function agregar(evento: FormEvent) {
        evento.preventDefault()
        setError(null)

        if (!linea) {
            setError('Elija el producto que se devuelve.')
            return
        }

        const valor = Number(cantidad.replace(',', '.'))

        if (!Number.isFinite(valor) || valor <= 0) {
            setError('La cantidad debe ser mayor que cero.')
            return
        }

        if (valor > disponible) {
            setError(
                `De ese producto quedan ${formatearCantidad(disponible, 3)} por devolver.`,
            )
            return
        }

        const listo = await alAgregar({
            ventaDetalleId: linea.ventaDetalleId,
            cantidad: valor,
            condicionProducto: condicion.trim() || undefined,
        })

        if (listo) {
            setDetalleId('')
            setCantidad('')
            setCondicion('')
        }
    }

    return (
        <form className="columna g3" onSubmit={agregar}>
            <h3>Agregar producto</h3>

            <Estado
                cargando={cargandoVenta}
                error={errorVenta}
                alReintentar={alReintentarVenta}
                vacio={venta === null}
                mensajeVacio="No se pudo leer la venta original: hace falta el permiso de registrar ventas."
            >
                <div className="filtros">
                    <div className="filtro-ancho">
                        <label className="etiqueta" htmlFor="linea-venta">
                            Producto de la venta
                        </label>
                        <select
                            id="linea-venta"
                            className="campo"
                            value={detalleId}
                            onChange={(e) => {
                                setDetalleId(e.target.value)
                                setCantidad('')
                            }}
                        >
                            <option value="">Elija el producto…</option>
                            {(venta?.lineas ?? []).map((l) => (
                                <option key={l.ventaDetalleId} value={l.ventaDetalleId}>
                                    {l.descripcion} — {formatearCantidad(l.cantidad, 3)}{' '}
                                    {l.unidadMedidaCodigo}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="etiqueta" htmlFor="cantidad-devuelta">
                            Cantidad{' '}
                            {linea && (
                                <span className="tenue">
                                    (quedan {formatearCantidad(disponible, 3)})
                                </span>
                            )}
                        </label>
                        <input
                            id="cantidad-devuelta"
                            className="campo campo-numero"
                            inputMode="decimal"
                            autoComplete="off"
                            value={cantidad}
                            onChange={(e) => setCantidad(e.target.value)}
                        />
                    </div>

                    <div>
                        <label className="etiqueta" htmlFor="condicion-producto">
                            Condición <span className="tenue">(opcional)</span>
                        </label>
                        <input
                            id="condicion-producto"
                            className="campo"
                            maxLength={100}
                            placeholder="Dañado de fábrica"
                            value={condicion}
                            onChange={(e) => setCondicion(e.target.value)}
                        />
                    </div>
                </div>

                <p className="tenue diminuto">
                    El producto devuelto <strong>no vuelve al stock vendible</strong>: se da por
                    perdido.
                </p>

                {error && (
                    <div className="aviso aviso-error" role="alert">
                        {error}
                    </div>
                )}

                <div className="fila">
                    <button type="submit" className="boton" disabled={ocupado}>
                        Agregar
                    </button>
                </div>
            </Estado>
        </form>
    )
}

/* -------------------------------------------------------------------------- */
/* Reembolso                                                                   */
/* -------------------------------------------------------------------------- */

function FormularioReembolso({
    devolucion,
    alConfirmada,
    alCancelar,
}: {
    devolucion: Devolucion
    alConfirmada: (devolucion: Devolucion) => void
    alCancelar: () => void
}) {
    const autorizadores = usarCargar(
        () => autenticacion.autorizadores('devolucion.autorizar'),
        [],
    )

    // Los medios los da el servidor: la lista escrita a mano solo tenia
    // efectivo y debito, y con los identificadores asumidos. Un reembolso a
    // una tarjeta de credito no tenia contra que registrarse.
    const medios = usarCargar(() => apiVentas.mediosPago(), [])
    const disponibles = medios.datos ?? []

    const [importes, setImportes] = useState<Record<number, string>>({})

    // Se parte con todo en efectivo, que es lo que ocurre en el mostrador. Se
    // hace al llegar la lista porque antes no se sabe cual es el efectivo.
    useEffect(() => {
        const efectivo = disponibles.find((m) => m.afectaEfectivo)

        if (efectivo && Object.keys(importes).length === 0) {
            setImportes({ [efectivo.medioPagoId]: String(devolucion.total) })
        }
    }, [disponibles, importes, devolucion.total])
    const [referencias, setReferencias] = useState<Record<number, string>>({})
    const [autorizador, setAutorizador] = useState('')
    const [error, setError] = useState<string | null>(null)
    const [enviando, setEnviando] = useState(false)

    const repartido = disponibles.reduce(
        (suma, m) => suma + (Number(importes[m.medioPagoId]) || 0),
        0,
    )
    const faltante = devolucion.total - repartido

    async function confirmar(evento: FormEvent) {
        evento.preventDefault()
        setError(null)

        if (faltante !== 0) {
            setError(
                faltante > 0
                    ? `Faltan ${dinero(faltante)} por repartir.`
                    : `Se repartieron ${dinero(-faltante)} de más.`,
            )
            return
        }

        if (!autorizador) {
            setError('Elija quién autoriza la devolución.')
            return
        }

        const reembolsos = disponibles.filter((m) => (Number(importes[m.medioPagoId]) || 0) > 0).map(
            (m) => ({
                medioPagoId: m.medioPagoId,
                importe: Math.round(Number(importes[m.medioPagoId])),
                referencia: referencias[m.medioPagoId]?.trim() || undefined,
            }),
        )

        const sinReferencia = disponibles.find(
            (m) =>
                m.requiereReferencia &&
                (Number(importes[m.medioPagoId]) || 0) > 0 &&
                !referencias[m.medioPagoId]?.trim(),
        )

        if (sinReferencia) {
            setError(`${sinReferencia.nombre} exige el número de operación.`)
            return
        }

        setEnviando(true)

        try {
            alConfirmada(
                await apiDevoluciones.confirmar(devolucion.devolucionId, {
                    reembolsos,
                    autorizadaPorUsuarioId: Number(autorizador),
                }),
            )
        } catch (fallo) {
            setError(mensajeDeError(fallo))
        } finally {
            setEnviando(false)
        }
    }

    return (
        <form className="panel columna g3" onSubmit={confirmar}>
            <h3>Cómo se le devuelve el dinero</h3>

            <p className="tenue chico">
                Lo reembolsado tiene que ser exactamente lo devuelto, y el efectivo sale de la
                caja abierta: por eso aparece en el arqueo del turno.
            </p>

            {disponibles.map((m) => (
                <div key={m.medioPagoId} className="columna g2">
                    <div>
                        <label className="etiqueta" htmlFor={`reembolso-${m.medioPagoId}`}>
                            {m.nombre}
                        </label>
                        <input
                            id={`reembolso-${m.medioPagoId}`}
                            className="campo campo-numero"
                            type="number"
                            min={0}
                            step={10}
                            inputMode="numeric"
                            value={importes[m.medioPagoId] ?? ''}
                            onChange={(e) =>
                                setImportes((actuales) => ({
                                    ...actuales,
                                    [m.medioPagoId]: e.target.value,
                                }))
                            }
                        />
                    </div>

                    {m.requiereReferencia && (Number(importes[m.medioPagoId]) || 0) > 0 && (
                        <div>
                            <label className="etiqueta" htmlFor={`referencia-${m.medioPagoId}`}>
                                N° de operación
                            </label>
                            <input
                                id={`referencia-${m.medioPagoId}`}
                                className="campo numero"
                                maxLength={100}
                                value={referencias[m.medioPagoId] ?? ''}
                                onChange={(e) =>
                                    setReferencias((actuales) => ({
                                        ...actuales,
                                        [m.medioPagoId]: e.target.value,
                                    }))
                                }
                            />
                        </div>
                    )}
                </div>
            ))}

            <div
                className={`aviso ${faltante === 0 ? 'aviso-ok' : 'aviso-atencion'} fila entre g3`}
            >
                <span>{faltante === 0 ? 'Cuadra' : faltante > 0 ? 'Falta repartir' : 'De más'}</span>
                <strong className="numero">{dinero(Math.abs(faltante))}</strong>
            </div>

            <div>
                <label className="etiqueta" htmlFor="autorizador-devolucion">
                    Autoriza
                </label>
                <Estado
                    cargando={autorizadores.cargando}
                    error={autorizadores.error}
                    alReintentar={autorizadores.recargar}
                    vacio={(autorizadores.datos ?? []).length === 0}
                    mensajeVacio="No hay usuarios con permiso para autorizar devoluciones."
                >
                    <select
                        id="autorizador-devolucion"
                        className="campo"
                        value={autorizador}
                        onChange={(e) => setAutorizador(e.target.value)}
                    >
                        <option value="">Elija quién autoriza…</option>
                        {(autorizadores.datos ?? []).map((a) => (
                            <option key={a.usuarioId} value={a.usuarioId}>
                                {a.nombreCompleto}
                            </option>
                        ))}
                    </select>
                </Estado>
            </div>

            {error && (
                <div className="aviso aviso-error" role="alert">
                    {error}
                </div>
            )}

            <div className="fila g2">
                <button type="button" className="boton" onClick={alCancelar}>
                    Volver
                </button>
                <button type="submit" className="boton boton-primario" disabled={enviando}>
                    {enviando ? 'Confirmando…' : 'Confirmar devolución'}
                </button>
            </div>
        </form>
    )
}

/* -------------------------------------------------------------------------- */

function DialogoAnularDevolucion({
    devolucion,
    alAnulada,
    alCerrar,
}: {
    devolucion: Devolucion
    alAnulada: () => void
    alCerrar: () => void
}) {
    const [motivo, setMotivo] = useState('')
    const [error, setError] = useState<string | null>(null)
    const [enviando, setEnviando] = useState(false)

    async function anular(evento: FormEvent) {
        evento.preventDefault()
        setError(null)

        if (motivo.trim().length === 0) {
            setError('La anulación exige indicar el motivo.')
            return
        }

        setEnviando(true)

        try {
            await apiDevoluciones.anular(devolucion.devolucionId, motivo.trim())
            alAnulada()
        } catch (fallo) {
            setError(mensajeDeError(fallo))
        } finally {
            setEnviando(false)
        }
    }

    return (
        <Dialogo
            titulo={`Anular ${devolucion.numero}`}
            alCerrar={alCerrar}
            acciones={
                <>
                    <button type="button" className="boton" onClick={alCerrar}>
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        form="forma-anular-devolucion"
                        className="boton boton-peligro"
                        disabled={enviando}
                    >
                        {enviando ? 'Anulando…' : 'Anular'}
                    </button>
                </>
            }
        >
            <form id="forma-anular-devolucion" className="columna g4" onSubmit={anular}>
                <div className="aviso aviso-atencion">
                    Se revierte el reembolso de {dinero(devolucion.total)} y la venta vuelve al
                    estado que le corresponda. La devolución queda registrada como anulada, no se
                    borra.
                </div>

                <div>
                    <label className="etiqueta" htmlFor="motivo-anular-devolucion">
                        Motivo
                    </label>
                    <input
                        id="motivo-anular-devolucion"
                        className="campo"
                        maxLength={500}
                        value={motivo}
                        onChange={(e) => setMotivo(e.target.value)}
                    />
                </div>

                {error && (
                    <div className="aviso aviso-error" role="alert">
                        {error}
                    </div>
                )}
            </form>
        </Dialogo>
    )
}
